import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PROFILE } from "../../data/portfolioData";
import { DownloadIcon } from "../ui/Icons";
import Socials from "../ui/Socials";

const Photo = () => {
  const [greeting, setGreeting] = useState(0);
  const next = () => setGreeting((g) => (g + 1) % PROFILE.greetings.length);

  return (
    <div className="w-full h-full relative">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { delay: 2, duration: 0.4, ease: "easeIn" } }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 2.4, duration: 0.4, ease: "easeInOut" } }}
          className="w-[298px] h-[298px] xl:w-[498px] xl:h-[498px] absolute"
        >
          <div className="w-full h-full rounded-full p-3">
            <img
              src={PROFILE.photo}
              alt={`${PROFILE.firstName} ${PROFILE.lastName}`}
              className="w-full h-full object-cover rounded-full"
            />
          </div>
        </motion.div>

        <button
          type="button"
          onClick={next}
          className="group relative block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={`Photo. Tap to change greeting: ${PROFILE.greetings[greeting]}`}
        >
          <motion.svg
            className="w-[300px] xl:w-[506px] h-[300px] xl:h-[506px]"
            fill="transparent"
            viewBox="0 0 506 506"
            xmlns="http://www.w3.org/2000/svg"
          >
            <motion.circle
              cx="253"
              cy="253"
              r="250"
              stroke="currentColor"
              className="text-accent"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ strokeDasharray: "24 10 0 0" }}
              animate={{
                strokeDasharray: ["15 120 25 25", "16 25 92 72", "4 250 22 22"],
                rotate: [120, 360],
              }}
              transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            />
          </motion.svg>
          <span className="pointer-events-none absolute left-1/2 top-[84%] -translate-x-1/2 rounded-full bg-ink/70 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-primary opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.span
                key={greeting}
                className="block whitespace-nowrap"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                {PROFILE.greetings[greeting]}
              </motion.span>
            </AnimatePresence>
          </span>
        </button>
      </motion.div>
    </div>
  );
};

const Hero = () => (
  <section className="relative">
    <div className="h-screen w-full bg-primary bg-grid absolute top-0 left-0 flex items-center justify-center pointer-events-none">
      <div className="absolute inset-0 bg-primary [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />
    </div>
    <div className="container mx-auto h-full relative z-10">
      <div className="flex flex-col xl:flex-row items-center justify-between xl:pt-8 xl:pb-24">
        <motion.div
          className="text-center xl:text-left order-2 xl:order-none"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.6 }}
        >
          <span className="text-xl">{PROFILE.title}</span>
          <h1 className="h1 mb-6">
            <span>Hello I'm</span> <br />
            <span className="text-accent relative inline-block group cursor-pointer overflow-hidden">
              <span className="relative z-10 inline-block transition-colors duration-500 group-hover:text-primary">
                {PROFILE.firstName} <br />
                {PROFILE.lastName}
              </span>
              <span className="absolute inset-0 bg-accent z-0 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />
            </span>
          </h1>
          <p className="max-w-[500px] mb-9 text-ink/70 mx-auto xl:mx-0">{PROFILE.tagline}</p>
          <div className="flex flex-col xl:flex-row items-center gap-8">
            <a
              href={PROFILE.resume}
              target="_blank"
              rel="noopener noreferrer"
              className="h-[56px] px-8 border border-accent rounded-full flex items-center justify-center gap-2 text-accent text-sm font-semibold uppercase tracking-[2px] hover:bg-accent hover:text-primary transition-all duration-200"
            >
              <span>View CV</span>
              <DownloadIcon size={20} />
            </a>
            <div className="mb-8 xl:mb-0">
              <Socials />
            </div>
          </div>
        </motion.div>
        <div className="order-1 xl:order-none mb-8 xl:mb-0">
          <Photo />
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
